import type {
  DiamModifierValues,
  DiamRoleAttribute,
  DiamRoleDetails,
} from '../../diamApi';
import type { Role } from '../RolesGrid/types';
import type { TeamRole } from '../TeamRolesGrid/types';
import { EMPTY_TEAM_NAME } from './constants';
import {
  collectSelectedPermissionIds,
  mapRolePermissionTree,
  roleDetailsUsesInclusionTree,
} from './mapRolePermissionTree';
import {
  ROLE_CATEGORIES,
  RoleCategory,
  RoleDetailsModel,
  RoleModifierField,
} from './types';

function attributeNames(attributes: DiamRoleAttribute[] | undefined): string[] {
  return (attributes ?? [])
    .map(attribute => attribute.name)
    .filter(name => Boolean(name));
}

function roleAttributeOrInclusionLabels(role: DiamRoleDetails): string[] {
  const directAttributes = attributeNames(role.attributes);
  if (directAttributes.length > 0) {
    return directAttributes;
  }

  return (role.inclusions ?? [])
    .map(inclusion => inclusion.roleName ?? inclusion.roleCode ?? undefined)
    .filter((name): name is string => Boolean(name));
}

function formatModifierLabel(modifierType: string): string {
  if (!modifierType) {
    return modifierType;
  }

  return modifierType.charAt(0).toUpperCase() + modifierType.slice(1);
}

function mapModifiers(role: DiamRoleDetails): RoleModifierField[] {
  const modifierTypes = [
    ...new Set([
      ...(role.requiredModifiers ?? []),
      ...(role.optionalModifiers ?? []),
      ...Object.keys(role.directModifiers ?? {}),
    ]),
  ];
  const values: DiamModifierValues = role.directModifiers ?? {};

  return modifierTypes.map(modifierType => ({
    id: modifierType,
    label: formatModifierLabel(modifierType),
    value: values[modifierType] ?? '',
    inputType: 'text' as const,
  }));
}

export function mapRoleCategory(role: DiamRoleDetails): RoleCategory {
  return role.type === 'TEAM'
    ? ROLE_CATEGORIES.custom
    : ROLE_CATEGORIES.predefined;
}

export function mapApiRoleToGridRole(role: DiamRoleDetails): Role {
  return {
    id: role.roleId,
    name: role.name,
    description: role.description ?? '',
    attributes: roleAttributeOrInclusionLabels(role),
    createdAt: role.createdDate ?? role.createdAt ?? '',
    updatedAt: role.lastModifiedDate ?? role.updatedAt ?? '',
  };
}

export function mapApiRoleToTeamRole(
  role: DiamRoleDetails,
  teamNameById?: ReadonlyMap<string, string>,
): TeamRole {
  const scope =
    role.team?.name ??
    (role.teamId ? teamNameById?.get(role.teamId) : undefined) ??
    EMPTY_TEAM_NAME;

  return {
    id: role.roleId,
    name: role.name,
    description: role.description ?? '',
    scope,
    roles: (role.inclusions ?? [])
      .map(inclusion => inclusion.roleName)
      .filter((roleName): roleName is string => Boolean(roleName)),
    attributes: roleAttributeOrInclusionLabels(role),
    createdAt: role.createdDate ?? role.createdAt ?? '',
    updatedAt: role.lastModifiedDate ?? role.updatedAt ?? '',
  };
}

export function mapApiRoleToDetails(role: DiamRoleDetails): RoleDetailsModel {
  const permissionTree = mapRolePermissionTree(role);
  const usesInclusionTree = roleDetailsUsesInclusionTree(role);

  return {
    id: role.roleId,
    category: mapRoleCategory(role),
    roleType: role.type,
    name: role.name,
    description: role.description ?? '',
    teamId: role.teamId ?? role.team?.id ?? '',
    teamName: role.team?.name ?? EMPTY_TEAM_NAME,
    usesInclusionTree,
    selectedPermissionIds: collectSelectedPermissionIds(role),
    permissionTree,
    modifiers: mapModifiers(role),
  };
}
